<script lang="ts">
  import { PlusCircle } from 'phosphor-svelte';
  import { userStore, setUIState } from '$lib/stores/userStore';
  import type { CreateUserRequest } from '$lib/models/user/types';
  import Button from '$lib/components/common/button/Button.svelte';
  import { getErrorMessage, isErrorResponse } from '$lib/utils/error/errorUtils';
  import { SlideToggle, getToastStore } from '@skeletonlabs/skeleton';
  import { onMount } from 'svelte';

  export let clientId: number;

  let name = '';
  let userName = '';
  let email = '';
  let firstPage = '';
  let isExemptedFromSync = false;
  let permissions: string[] = [];
  let isActive = true;
  let userPhoto: string | null = null;
  let password = '';
  let availablePermissions: Array<{ key: number; value: string }> = [];
  let loading = false;

  const toastrStore = getToastStore();

  const firstPageOptions = [
    'Create Claim',
    'Invoice',
    'Expense Claim Pipeline',
    'Invoice Upload',
    'Expense Claim',
    'Credit Cards',
    'Purchase Order Pipeline',
    'My Dashboard',
    'My Queue',
    'Create PO',
    'Invoice Pipeline',
    'Purchase Order',
  ].sort();

  async function checkUserState() {
    const userCount = await userStore.count(clientId);
    setUIState(userCount > 0 ? 'DataTable' : 'NoUserPage');
  }

  function cancel(): void {
    checkUserState();
  }

  async function createUser(): Promise<void> {
    const newUser: CreateUserRequest = {
      clientId: clientId,
      name,
      userName,
      email,
      password,
      isActive,
      userPhoto: userPhoto ? userPhoto.split(',')[1] : null,
      firstPage,
      isExemptedFromSync,
      permissions,
    };

    loading = true;

    try {
      await userStore.create(newUser);
      checkUserState();
    } catch (errors) {
      for (let error of errors) {
        toastrStore.trigger({
          message: `${error.key} - ${error.value}`,
          background: 'bg-error-500',
          classes: 'text-white',
        });
      }
    } finally {
      loading = false;
    }
  }

  function handleFileUpload(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      resizeImage(file, 64, 64)
        .then((resizedImage) => {
          userPhoto = resizedImage;
        })
        .catch((error) => {
          toastrStore.trigger({
            message: error,
            background: 'bg-error-500',
          });
        });
    }
  }

  async function resizeImage(file: File, width: number, height: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL(file.type));
          } else {
            reject('Failed to get canvas context.');
          }
        };
        img.src = event.target?.result as string;
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  onMount(async () => {
    try {
      const result = await userStore.fetchPermissions();
      if (isErrorResponse(result)) {
        error = getErrorMessage(result);
      } else {
        availablePermissions = result;
      }
    } catch (err) {
      error = 'Error fetching permissions: ' + err.message;
    }
  });
</script>

<div class="card my-6 bg-[#FAFAFC] p-6">
  <div class="mb-5 text-lg font-bold text-primary-500">Add New User</div>

  <div class="grid grid-cols-4 gap-4 border border-[#EEEEEE] bg-[#FFFFFF] p-5 rounded-container-token">
    <!-- Avatar -->
    <div class="col-span-1 flex flex-col items-center justify-center">
      <label for="avatar" class="mb-2 text-sm text-gray-700">Avatar</label>
      <input type="file" id="avatar" accept="image/*" hidden on:change={handleFileUpload} />
      <label for="avatar" class="flex h-16 w-16 cursor-pointer items-center justify-center rounded-full border-2 border-gray-300">
        {#if userPhoto}
          <img src={userPhoto} alt="avatar" class="h-16 w-16 rounded-full object-cover" />
        {:else}
          <PlusCircle class="h-8 w-8 text-primary-500" />
        {/if}
      </label>
    </div>

    <!-- Name -->
    <div class="col-span-1 flex flex-col">
      <label for="name" class="mb-2 text-sm text-gray-700">Name</label>
      <input type="text" id="name" placeholder="" bind:value={name} class="input" />
    </div>

    <!-- Username -->
    <div class="col-span-1 flex flex-col">
      <label for="userName" class="mb-2 text-sm text-gray-700">Username</label>
      <input type="email" id="userName" placeholder="" bind:value={userName} class="input" />
    </div>

    <!-- Generated Password -->
    <div class="col-span-1 flex flex-col">
      <label for="generatedPassword" class="mb-2 text-sm text-gray-700">Initial Password</label>
      <input type="password" id="generatedPassword" placeholder="" bind:value={password} class="input" />
    </div>

    <!-- Email -->
    <div class="col-span-1 flex flex-col">
      <label for="email" class="mb-2 text-sm text-gray-700">Email</label>
      <input type="email" id="email" placeholder="" bind:value={email} class="input" />
    </div>

    <!-- First Page -->
    <div class="col-span-1 flex flex-col">
      <label for="firstPage" class="mb-2 text-sm text-gray-700">First Page</label>
      <select id="firstPage" bind:value={firstPage} class="select">
        <option value="" disabled selected>Select First Page</option>
        {#each firstPageOptions as option}
          <option value={option}>{option}</option>
        {/each}
      </select>
    </div>

    <!-- Permissions -->
    <div class="col-span-1 flex flex-col">
      <label for="permissions" class="mb-2 text-sm text-gray-700">Permissions</label>
      <select id="permissions" bind:value={permissions} class="select" multiple>
        {#each availablePermissions as { key, value }}
          <option {value}>{value}</option>
        {/each}
      </select>
    </div>

    <!-- Is Exempted From Sync -->
    <div class="col-span-1 flex flex-col">
      <label for="isExemptedFromSync" class="mb-2 text-sm text-gray-700">Exempt from Sync</label>
      <SlideToggle name="isExemptedFromSync" bind:checked={isExemptedFromSync} active="bg-success-500" background="bg-secondary-600" />
    </div>

    <!-- Action Buttons -->
    <div class="col-span-4 mt-4 flex justify-end space-x-4">
      <Button label="Cancel" size="medium" variant="outlined" onClick={cancel} />
      <Button label="Create User" colour="primary" size="medium" onClick={createUser} disabled={loading} />
    </div>
  </div>
</div>
